import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { IBudgetCalculateTotalResponse } from '../../../../core/models/budget-calculate-total-response.model';
import { IBudgetResponseModel } from '../../interfaces/budget-response.model';
import { IOptionsAttributeValueResponseModel } from '../../interfaces/options-attribute-value-response.model';
import { OptionsApprovalService } from '../../services/options-approval.service';
import { flatMap } from 'lodash';
import { IConfigProductHierarchyResponseModel } from '../../interfaces/config-product-hierarchy-response.model';

@Component({
    selector: 'lynkd-pattern-totals-table',
    templateUrl: './totals-table.component.html',
    styleUrls: ['./totals-table.component.scss']
})
export class TotalsTableComponent implements OnInit, OnChanges {
    @Input()
    public filteredAttributeValues: Array<IOptionsAttributeValueResponseModel> = [];

    @Input()
    public attributeValue: string;

    @Input()
    public availableBrandValues: Array<IConfigProductHierarchyResponseModel>;

    @Input()
    public timeValue: string;

    @Input()
    public set loading(data: boolean) {
        if (data) {
            this._loading = data;
            return;
        }

        this._loading = false;
    }

    public get loading(): boolean {
        return this._loading;
    }

    @Output()
    public readonly state: EventEmitter<{
        attributeValueId: number;
        budgetChange: Array<IBudgetCalculateTotalResponse>;
    }> = new EventEmitter();

    public data: IBudgetCalculateTotalResponse = {
        id: 0,
        core_budget: 0,
        core_percentage: 0,
        fashion_budget: 0,
        fashion_percentage: 0,
        actual_budget: 0,
        actual_percentage: 0,
        total_budget: 0,
        productLevelId: '',
        productLevelName: ''
    };

    public columns: Array<string> = ['total', 'core', 'fashion', 'actual'];
    public totalColumns: Set<{
        name: Exclude<keyof IBudgetResponseModel, 'id' | 'productLevelId' | 'productLevelName'> | 'fashionValue' | 'coreValue';
        type: 'percentage' | 'decimal';
    }> = new Set<{
        name: Exclude<keyof IBudgetResponseModel, 'id' | 'productLevelId' | 'productLevelName'> | 'fashionValue' | 'coreValue';
        type: 'percentage' | 'decimal';
    }>([
        {
            name: 'totalBudget',
            type: 'decimal'
        },
        {
            name: 'corePercentage',
            type: 'percentage'
        },
        {
            name: 'coreValue',
            type: 'decimal'
        },
        {
            name: 'fashionPercentage',
            type: 'percentage'
        },
        {
            name: 'fashionValue',
            type: 'decimal'
        }
    ]);
    private _loading: boolean;

    public constructor(private readonly _optionsApprovalService: OptionsApprovalService) {}

    public ngOnInit(): void {
        this.initializeBudgetsTable();
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes.tYOptionsApprovalData) {
            this.initializeBudgetsTable();
        }
    }

    public updateBudgetCell(
        attributeValue: IOptionsAttributeValueResponseModel,
        budget: IBudgetResponseModel,
        updateType: 'corePercentage' | 'fashionPercentage' | 'totalBudget',
        value: number = 0
    ): void {
        budget[updateType] = value;
        this.loading = true;
        try {
            if (updateType === 'corePercentage') {
                budget.fashionPercentage = 100 - budget.corePercentage;
            } else if (updateType === 'fashionPercentage') {
                budget.corePercentage = 100 - budget.fashionPercentage;
            }
            this.updateState({
                attributeValueId: attributeValue.id,
                budgetChange: this.mapOptionToBudgetCalculateTotal(attributeValue.budgets)
            });
            this.loading = false;
        } catch (error) {
            this.loading = false;
        }
    }

    public getColumnValue(
        col: 'total' | 'core' | 'fashion' | 'actual',
        budget: IBudgetResponseModel,
        isPercentage?: boolean
    ): number {
        if (col === 'total') {
            return budget.totalBudget;
        } else if (col === 'core') {
            if (isPercentage) {
                return budget.corePercentage;
            } else {
                return (budget.corePercentage / 100) * budget.totalBudget;
            }
        } else if (col === 'fashion') {
            if (isPercentage) {
                return budget.fashionPercentage;
            } else {
                return (budget.fashionPercentage / 100) * budget.totalBudget;
            }
        } else if (col === 'actual') {
            return 0;
        }
    }

    public updateState(budgetUpdates: { attributeValueId: number; budgetChange: Array<IBudgetCalculateTotalResponse> }): void {
        this.state.emit(budgetUpdates);
    }

    public mapOptionToBudgetCalculateTotal(budgets: Array<IBudgetResponseModel>): Array<IBudgetCalculateTotalResponse> {
        return budgets.map((budget: IBudgetResponseModel) => ({
            id: budget.id,
            core_budget: budget.corePercentage && budget.totalBudget ? (budget.totalBudget * budget.corePercentage) / 100 : 0,
            core_percentage: budget.corePercentage,
            fashion_budget:
                budget.fashionPercentage && budget.totalBudget ? (budget.totalBudget * budget.fashionPercentage) / 100 : 0,
            fashion_percentage: budget.fashionPercentage,
            actual_budget: 0, // TODO: Where to find Actual Budget. Is this to be calculated?
            actual_percentage: 0, // TODO: Where to find Actual Budget. Is this to be calculated?
            total_budget: budget.totalBudget,
            productLevelName: budget.productLevelName,
            productLevelId: budget.productLevelId
        }));
    }

    public initializeBudgetsTable(): void {
        if (this.filteredAttributeValues) {
            for (const attributeValue of this.attributeValue === 'ALL' ? this.availableBrandValues : [this.attributeValue]) {
                // We're working with a single attribute here, so filter out that attribute value data and pass
                // it to the rest of the function
                const tyOptionsAttributeValueData: IOptionsAttributeValueResponseModel = this.filteredAttributeValues.find(
                    (optionsAttributeValue: IOptionsAttributeValueResponseModel) =>
                        optionsAttributeValue.attributeValue === attributeValue
                );

                if (tyOptionsAttributeValueData && tyOptionsAttributeValueData.budgets.length > 0) {
                    // this.data = this.mapOptionToBudgetCalculateTotal(tyOptionsAttributeValueData.budget);
                    this.updateState({
                        attributeValueId: tyOptionsAttributeValueData.id,
                        budgetChange: this.mapOptionToBudgetCalculateTotal(tyOptionsAttributeValueData.budgets)
                    });
                }
            }
        }
    }

    public transformNumberToBeThousandSeparated(value: number): string {
        if (!value && value !== 0) return '';
        return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    }

    public transformNumberToRemoveThousandSeparator(value: string): number {
        const numericValue: string = value.replace(/ /g, '');
        return numericValue ? Number(numericValue) : 0;
    }

    public transformNumberToPercentage(value: number): string {
        return value ? `${value} %` : '';
    }

    public transformPercentageToNumber(value: string): number {
        return value ? Number(value.replace(/ %/g, '')) : 0;
    }

    public getShortId(id: string): string {
        return this._optionsApprovalService.getShortId(id);
    }

    public calculateTotal(
        columnName:
            | Exclude<keyof IBudgetResponseModel, 'id' | 'productLevelId' | 'productLevelName'>
            | 'fashionValue'
            | 'coreValue'
    ): number {
        const budgets: Array<IBudgetResponseModel> = flatMap(
            this.filteredAttributeValues,
            (attributeValue: IOptionsAttributeValueResponseModel) => attributeValue.budgets
        );

        let getRowValue: (budget: IBudgetResponseModel) => number;
        if (columnName === 'fashionValue') {
            getRowValue = (budget: IBudgetResponseModel): number => this.getColumnValue('fashion', budget);
        } else if (columnName === 'coreValue') {
            getRowValue = (budget: IBudgetResponseModel): number => this.getColumnValue('core', budget);
        } else {
            getRowValue = (budget: IBudgetResponseModel): number => budget[columnName];
        }
        let total: number = budgets.reduce((sum: number, budget: IBudgetResponseModel) => {
            const rowValue: number = getRowValue(budget);
            return sum + rowValue;
        }, 0);

        if (columnName === 'corePercentage' || columnName === 'fashionPercentage') {
            total = total / budgets.length;
        }
        return Math.round((total + Number.EPSILON) * 100) / 100;
    }
}
